import React, { useMemo } from "react";
import { Select } from "antd";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";

const COUNTRY_NAMES = new Intl.DisplayNames(["en"], { type: "region" });

const ALL_OPTIONS = getCountries().map((iso2) => {
    const dialCode = getCountryCallingCode(iso2);
    let name;
    try {
        name = COUNTRY_NAMES.of(iso2) || iso2;
    } catch {
        name = iso2;
    }
    return {
        value: `+${dialCode}`,
        iso2: iso2.toLowerCase(),
        name,
        dialCode: `+${dialCode}`,
        search: `+${dialCode} ${name.toLowerCase()} ${iso2.toLowerCase()}`,
    };
});

// Deduplicate by dial code
const SEEN = new Set();
const OPTIONS = ALL_OPTIONS.filter((opt) => {
    if (SEEN.has(opt.value)) return false;
    SEEN.add(opt.value);
    return true;
}).sort((a, b) => parseInt(a.dialCode) - parseInt(b.dialCode));

const FLAG_STYLE = { width: 20, height: 15, borderRadius: 2, objectFit: "cover", flexShrink: 0 };

function FlagImg({ iso2, alt }) {
    return (
        <img
            src={`https://flagcdn.com/20x15/${iso2}.png`}
            srcSet={`https://flagcdn.com/40x30/${iso2}.png 2x`}
            width={20}
            height={15}
            alt={alt}
            style={FLAG_STYLE}
        />
    );
}

// Plain string labels — used by AntD for built-in text search, no JSX, no image requests
const SELECT_OPTIONS = OPTIONS.map((opt) => ({
    value: opt.value,
    label: `${opt.dialCode} ${opt.name}`,
}));

const OPT_BY_VALUE = Object.fromEntries(OPTIONS.map((o) => [o.value, o]));

const PhoneCodeSelect = ({ value, onChange, placeholder, style }) => {
    const selectedOpt = value ? OPT_BY_VALUE[value] : null;

    return (
        <Select
            showSearch
            value={value || undefined}
            onChange={onChange}
            placeholder={placeholder || "+971"}
            style={style || { width: "100%" }}
            allowClear
            filterOption={(input, option) => {
                const opt = OPT_BY_VALUE[option?.value];
                return opt ? opt.search.includes(input.toLowerCase()) : false;
            }}
            options={SELECT_OPTIONS}
            // optionRender fires only for visible rows in the virtual scroll window
            optionRender={(option) => {
                const opt = OPT_BY_VALUE[option.value];
                if (!opt) return option.label;
                return (
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <FlagImg iso2={opt.iso2} alt={opt.name} />
                        <span>{opt.dialCode}</span>
                        <span style={{ color: "#888", fontSize: 12 }}>{opt.name}</span>
                    </span>
                );
            }}
            labelRender={() =>
                selectedOpt ? (
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <FlagImg iso2={selectedOpt.iso2} alt={selectedOpt.name} />
                        <span>{selectedOpt.dialCode}</span>
                    </span>
                ) : null
            }
        />
    );
};

export { PhoneCodeSelect };
